import { busyTracker } from './busyTracker';

/**
 * The single gate through which every full-page reload passes — both the new-version
 * auto-refresh and the expired-session redirect.
 *
 * Why one gate: it guarantees a deploy or an expired token can never interrupt an
 * interruption-sensitive operation (a bulk upload or bulk delete). If one is running, the
 * reload is deferred until `busyTracker` drains to zero; otherwise it happens immediately.
 * A single `scheduled` guard means exactly one reload ever fires, eliminating the
 * double-reload race that would otherwise be possible when the version watcher and a 401
 * fire at once.
 *
 * There is deliberately no time-based fail-safe: a legitimate bulk operation can run for a
 * long time, and forcing a reload on a timer would be the very interruption we are avoiding.
 * `busyTracker` is always released via try/finally at the call sites, so "wait until idle"
 * cannot get permanently stuck by normal code paths.
 */

let scheduled = false;
let unsubscribe: (() => void) | null = null;

function doReload(): void {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
}

/**
 * Reload the page now if idle, or as soon as the in-flight operation finishes.
 * Idempotent: the first call wins; subsequent calls are no-ops.
 *
 * @param _reason free-form tag for intent (e.g. 'new_version', 'session_expired');
 *                currently informational only.
 */
export function requestReload(_reason?: string): void {
  if (scheduled) return;
  scheduled = true;

  if (!busyTracker.isActive()) {
    doReload();
    return;
  }

  // An operation is running: reload as soon as activity drains to zero.
  unsubscribe = busyTracker.subscribe(() => {
    if (!busyTracker.isActive()) {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      doReload();
    }
  });
}

/** True once a reload has been requested (lets callers suppress duplicate work/UI). */
export function isReloadScheduled(): boolean {
  return scheduled;
}
